import { useEffect, useState, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  monitoringService,
  ErrorRecord,
  ErrorsResponse,
  ErrorsFilters,
  ErrorsFilterOptions
} from '@/services/monitoring'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  RefreshCw,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  X,
  Copy,
} from 'lucide-react'
import { toast } from 'sonner'

// Integration status enum mapping
const INTEGRATION_STATUS_MAP: Record<number, string> = {
  0: 'credential_error',
  1: 'active',
  2: 'no_content',
  3: 'error',
  4: 'oauth_stub',
  5: 'verifying',
  6: 'revoked',
  7: 'pending_credentials'
}

const getStatusName = (status: number): string => {
  return INTEGRATION_STATUS_MAP[status] || `unknown_${status}`
}

const getStatusBadgeVariant = (status: number): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 1: return 'default' // active
    case 0: case 6: case 3: return 'destructive' // credential_error, revoked, error
    case 2: case 5: return 'secondary' // no_content, verifying
    default: return 'outline'
  }
}

// Job status display helper
const getJobStatusName = (status: string | number | null): string => {
  if (status === null || status === undefined) return '-'
  // Handle both string and number status values
  if (typeof status === 'string') return status
  // Legacy numeric mapping
  const numericMap: Record<number, string> = {
    0: 'queued',
    1: 'running',
    2: 'completed',
    3: 'failed',
    4: 'ghost',
    5: 'recovered',
    6: 'exhausted'
  }
  return numericMap[status] || 'unknown'
}

const getJobStatusBadgeVariant = (status: string | number | null): 'default' | 'secondary' | 'destructive' | 'outline' => {
  const statusStr = typeof status === 'string' ? status : getJobStatusName(status)
  switch (statusStr) {
    case 'completed': return 'default'
    case 'running': return 'secondary'
    case 'failed':
    case 'ghost':
    case 'exhausted': return 'destructive'
    case 'recovered': return 'secondary'
    case 'queued':
    default: return 'outline'
  }
}


type SortField = 'batch_timestamp' | 'organization_name' | 'integration_id' | 'integration_name' | 'integrator_slug' | 'error_category' | 'integration_status'
type SortDir = 'asc' | 'desc'

interface ActiveFilters {
  organization_ids: number[]
  integrator_slugs: string[]
  categories: string[]
  statuses: number[]
  batches: string[]
}

interface ErrorsProps {
  organizationId?: number
}

/**
 * Errors Tab - Comprehensive error investigation table
 *
 * Shows all failed/exhausted job periods with:
 * - Period selector (time range)
 * - Sortable columns
 * - Multi-select column filters
 * - Pagination
 * - Backfill action per row
 */
export function Errors({ organizationId }: ErrorsProps) {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const [errors, setErrors] = useState<ErrorRecord[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterOptions, setFilterOptions] = useState<ErrorsFilterOptions>({
    organizations: [],
    integrators: [],
    categories: [],
    statuses: [],
    batches: []
  })

  // Period selector
  const [hours, setHours] = useState<number>(24)

  // Pagination
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)

  // Sorting
  const [sortBy, setSortBy] = useState<SortField>('batch_timestamp')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  // Filters
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    organization_ids: [],
    integrator_slugs: [],
    categories: [],
    statuses: [],
    batches: []
  })

  // Prevent double fetch in StrictMode
  const hasFetched = useRef(false)

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
    fetchErrors()
  }, [])

  // Refetch when filters, sorting, or pagination changes
  useEffect(() => {
    if (!hasFetched.current) return
    fetchErrors()
  }, [hours, page, sortBy, sortDir, activeFilters, organizationId])

  const fetchErrors = async () => {
    try {
      setLoading(true)
      setError(null)

      // Use parent's organizationId if provided, otherwise use activeFilters
      const orgFilter = organizationId
        ? [organizationId]
        : (activeFilters.organization_ids.length > 0 ? activeFilters.organization_ids : undefined)

      const filters: ErrorsFilters = {
        hours,
        page,
        size: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
        organization_ids: orgFilter,
        integrator_slugs: activeFilters.integrator_slugs.length > 0 ? activeFilters.integrator_slugs : undefined,
        categories: activeFilters.categories.length > 0 ? activeFilters.categories : undefined,
        statuses: activeFilters.statuses.length > 0 ? activeFilters.statuses : undefined,
        batches: activeFilters.batches.length > 0 ? activeFilters.batches : undefined,
      }

      const response = await monitoringService.getErrors(filters)
      setErrors(response.errors)
      setTotal(response.total)
      setFilterOptions(response.filter_options)
    } catch (err: any) {
      console.error('Failed to fetch errors:', err)
      setError(err.response?.data?.detail || 'Failed to load errors')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    fetchErrors()
  }

  const copyToClipboard = () => {
    if (errors.length === 0) return

    const formatted = `# Errors Report (${errors.length} errors)
Time range: Last ${hours} hours
Generated: ${new Date().toISOString()}

| Job ID | Int. ID | Integration | Integrator | Organization | Status | Batch | Error | Category |
|---|---|---|---|---|---|---|---|---|
${errors.map(e => {
  const batch = e.batch_timestamp ? new Date(e.batch_timestamp).toISOString().slice(5, 13).replace('T', ' ') : '-'
  const error = e.error_message || '-'
  return `| ${e.job_id} | ${e.integration_id} | ${e.integration_name || '-'} | ${e.integrator_slug || '-'} | ${e.organization_name || '-'} | ${getJobStatusName(e.job_status)} | ${batch} | ${error} | ${e.error_category || '-'} |`
}).join('\n')}

Please analyze these errors and suggest remediation steps.`

    navigator.clipboard.writeText(formatted)
    toast.success('Errors copied to clipboard')
  }

  const handleHoursChange = (value: string) => {
    setHours(parseInt(value))
    setPage(1) // Reset to first page
  }

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortDir(field === 'batch_timestamp' ? 'desc' : 'asc')
    }
    setPage(1)
  }

  const renderSortIcon = (field: SortField) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />
    }
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />
  }

  const handleFilterChange = (
    filterKey: keyof ActiveFilters,
    value: number | string,
    checked: boolean
  ) => {
    setActiveFilters(prev => {
      const currentValues = prev[filterKey] as (number | string)[]
      if (checked) {
        return { ...prev, [filterKey]: [...currentValues, value] }
      } else {
        return { ...prev, [filterKey]: currentValues.filter(v => v !== value) }
      }
    })
    setPage(1)
  }

  const clearFilter = (filterKey: keyof ActiveFilters) => {
    setActiveFilters(prev => ({ ...prev, [filterKey]: [] }))
    setPage(1)
  }

  const clearAllFilters = () => {
    setActiveFilters({
      organization_ids: [],
      integrator_slugs: [],
      categories: [],
      statuses: [],
      batches: []
    })
    setPage(1)
  }

  const hasActiveFilters = () => {
    return (
      activeFilters.organization_ids.length > 0 ||
      activeFilters.integrator_slugs.length > 0 ||
      activeFilters.categories.length > 0 ||
      activeFilters.statuses.length > 0 ||
      activeFilters.batches.length > 0
    )
  }

  const totalPages = Math.ceil(total / pageSize)

  const formatBatchTime = (timestamp: string | null) => {
    if (!timestamp) return '-'
    const date = new Date(timestamp)
    // Format as MMDDHH to match Live Collection Monitor
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    const hour = String(date.getUTCHours()).padStart(2, '0')
    return `${month}${day}${hour}`
  }

  // Filter popup component
  const FilterPopup = ({
    label,
    filterKey,
    options,
    selectedValues,
    getLabel = (v: any) => String(v)
  }: {
    label: string
    filterKey: keyof ActiveFilters
    options: any[]
    selectedValues: (number | string)[]
    getLabel?: (value: any) => string
  }) => {
    const hasFilter = selectedValues.length > 0
    const noneSelected = selectedValues.length === 0

    const handleSelectAll = () => {
      const allValues = options.map(opt => typeof opt === 'object' ? opt.id : opt)
      setActiveFilters(prev => ({ ...prev, [filterKey]: allValues }))
      setPage(1)
    }

    const handleClear = () => {
      clearFilter(filterKey)
      setPage(1)
    }

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`h-6 px-2 ${hasFilter ? 'bg-primary/10 text-primary' : ''}`}
          >
            <Filter className="h-3 w-3" />
            {hasFilter && <span className="ml-1 text-xs">{selectedValues.length}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{label}</span>
            {noneSelected ? (
              <Button variant="ghost" size="sm" onClick={handleSelectAll} className="h-6 px-2 text-xs">
                All
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={handleClear} className="h-6 px-2 text-xs">
                Clear
              </Button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {options.length === 0 ? (
              <p className="text-sm text-muted-foreground">No options available</p>
            ) : (
              options.map((option, idx) => {
                const value = typeof option === 'object' ? option.id : option
                const label = getLabel(option)
                const isChecked = selectedValues.includes(value)

                return (
                  <div key={idx} className="flex items-center space-x-2">
                    <Checkbox
                      id={`${filterKey}-${value}`}
                      checked={isChecked}
                      onCheckedChange={(checked) =>
                        handleFilterChange(filterKey, value, checked === true)
                      }
                    />
                    <Label
                      htmlFor={`${filterKey}-${value}`}
                      className="text-sm cursor-pointer truncate"
                      title={label}
                    >
                      {label}
                    </Label>
                  </div>
                )
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <p className="text-lg font-semibold">Failed to load errors</p>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
            <Button onClick={handleRefresh} className="mt-4">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Errors
              <Badge variant="destructive">{total}</Badge>
            </CardTitle>
            <CardDescription>
              Failed and exhausted job periods
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Clear Filters */}
            {hasActiveFilters() && (
              <Button variant="outline" size="sm" onClick={clearAllFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </Button>
            )}

            {/* Copy to Clipboard */}
            <Button
              variant="outline"
              size="sm"
              onClick={copyToClipboard}
              disabled={errors.length === 0}
              title="Copy to clipboard"
            >
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>

            {/* Period Selector */}
            <Select value={String(hours)} onValueChange={handleHoursChange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last Batch</SelectItem>
                <SelectItem value="24">Last 24 hours</SelectItem>
                <SelectItem value="48">Last 48 hours</SelectItem>
                <SelectItem value="72">Last 3 days</SelectItem>
                <SelectItem value="168">Last 7 days</SelectItem>
                <SelectItem value="336">Last 14 days</SelectItem>
                <SelectItem value="720">Last 30 days</SelectItem>
              </SelectContent>
            </Select>

            {/* Refresh */}
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <div className="flex items-center justify-between">
                      <span
                        className="cursor-pointer hover:underline"
                        onClick={() => handleSort('batch_timestamp')}
                      >
                        Batch
                      </span>
                      <FilterPopup
                        label="Filter by Batch"
                        filterKey="batches"
                        options={filterOptions.batches}
                        selectedValues={activeFilters.batches}
                      />
                      {renderSortIcon('batch_timestamp')}
                    </div>
                  </TableHead>
                  <TableHead className="w-[70px]">
                    <span>Job ID</span>
                  </TableHead>
                  <TableHead className="w-[70px]">
                    <div className="flex items-center">
                      <span
                        className="cursor-pointer hover:underline"
                        onClick={() => handleSort('integration_id')}
                      >
                        Int. ID
                      </span>
                      {renderSortIcon('integration_id')}
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center justify-between">
                      <span
                        className="cursor-pointer hover:underline"
                        onClick={() => handleSort('integration_name')}
                      >
                        Integration
                      </span>
                      {renderSortIcon('integration_name')}
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center justify-between">
                      <span
                        className="cursor-pointer hover:underline"
                        onClick={() => handleSort('integrator_slug')}
                      >
                        Integrator
                      </span>
                      <FilterPopup
                        label="Filter by Integrator"
                        filterKey="integrator_slugs"
                        options={filterOptions.integrators}
                        selectedValues={activeFilters.integrator_slugs}
                      />
                      {renderSortIcon('integrator_slug')}
                    </div>
                  </TableHead>
                  <TableHead className="w-[80px]">
                    <span>Behavior</span>
                  </TableHead>
                  <TableHead>
                    <span>Status</span>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center justify-between">
                      <span
                        className="cursor-pointer hover:underline"
                        onClick={() => handleSort('error_category')}
                      >
                        Category
                      </span>
                      <FilterPopup
                        label="Filter by Category"
                        filterKey="categories"
                        options={filterOptions.categories}
                        selectedValues={activeFilters.categories}
                      />
                      {renderSortIcon('error_category')}
                    </div>
                  </TableHead>
                  <TableHead className="w-[800px] min-w-[400px]">Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && errors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Loading errors...</p>
                    </TableCell>
                  </TableRow>
                ) : errors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <p className="text-muted-foreground">No errors found in the selected time range</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  errors.map((err) => (
                    <TableRow key={err.job_id}>
                      <TableCell className="font-mono text-xs whitespace-nowrap">
                        {formatBatchTime(err.batch_timestamp)}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {err.job_id}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        <Link
                          to={`/${orgSlug}/integrations/${err.integration_id}/edit`}
                          className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          {err.integration_id}
                        </Link>
                      </TableCell>
                      <TableCell className="font-medium">{err.integration_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {err.integrator_slug || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {err.behavior_category === 'learning' ? 'hourly' : (err.behavior_category || '-')}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={getJobStatusBadgeVariant(err.job_status)}
                          className="text-xs"
                        >
                          {getJobStatusName(err.job_status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {err.error_category || '-'}
                      </TableCell>
                      <TableCell className="w-[800px] min-w-[400px]">
                        <span className="text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap break-all">
                          {err.error_message || '-'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <span className="text-sm text-muted-foreground">
                Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, total)} of {total} errors
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1 || loading}
                >
                  Previous
                </Button>
                <span className="text-sm">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === totalPages || loading}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
    </Card>
  )
}
